import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";

// --- Type Definitions ---
export interface ITestimonialV1Image {
  ImageUrl: string;
}

export interface ITestimonialV1Item {
  Description: string;
  Title: string;
  Subtitle: string;
}

export interface ITestimonialV1Widget {
  type: string; // should be WidgetType.TestimonalV1 in parent
  ImageUrl: string; // Main image for the testimonial widget
  Items: [ITestimonialV1Image, ITestimonialV1Item]; // Exactly two elements
}

// --- Props for the TestimonialV1Item component ---
interface TestimonialV1ItemProps {
  item: ITestimonialV1Widget;
  error: any;
  onChange: (updatedItem: ITestimonialV1Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const TestimonialV1Item: React.FC<TestimonialV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Ensure main ImageUrl is a string.
  const mainImage: string = typeof item.ImageUrl === "string" ? item.ImageUrl : "";

  // For the tuple Items, ensure defaults if missing.
  const authorImage: ITestimonialV1Image =
    item.Items && item.Items[0] ? item.Items[0] : { ImageUrl: "" };
  const testimonialContent: ITestimonialV1Item =
    item.Items && item.Items[1]
      ? item.Items[1]
      : { Description: "", Title: "", Subtitle: "" };

  // Local state for controlling image pickers.
  const [mainImagePickerOpen, setMainImagePickerOpen] = useState<boolean>(false);
  const [authorImagePickerOpen, setAuthorImagePickerOpen] = useState<boolean>(false);

  // Helper: if the parameter is an event, extract its target.value; otherwise, use it directly.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handler for main image.
  const handleMainImageChange = (selected: string | string[]) => {
    const newImage = Array.isArray(selected) ? selected[0] : selected;
    onChange({ ...item, ImageUrl: newImage }, "ImageUrl");
    setMainImagePickerOpen(false);
  };

  // Handler for author image (Section 1).
  const handleAuthorImageChange = (selected: string | string[]) => {
    const newImage = Array.isArray(selected) ? selected[0] : selected;
    // Update the first tuple element.
    const updatedItems: [ITestimonialV1Image, ITestimonialV1Item] = [
      { ...authorImage, ImageUrl: newImage },
      testimonialContent,
    ];
    onChange({ ...item, Items: updatedItems }, "Items");
    setAuthorImagePickerOpen(false);
  };

  // Handler for testimonial content (Section 2).
  const handleContentChange = (
    field: keyof ITestimonialV1Item,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedContent = { ...testimonialContent, [field]: newValue };
    onChange({ ...item, Items: [authorImage, updatedContent] }, "Items");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Testimonial Widget
        </Typography>

        {/* Main Image */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Main Image
        </Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
            cursor: "pointer",
            mb: 2,
          }}
          onClick={() => setMainImagePickerOpen(true)}
        >
          {mainImage ? (
            <img
              src={mainImage}
              alt="Main Testimonial"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Select Main Image
            </Typography>
          )}
        </Box>
        {error?.ImageUrl && (
          <Typography variant="body2" color="error">
            {error.ImageUrl}
          </Typography>
        )}

        {/* Section 1: Author Image */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Author Image
        </Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9f9f9",
            cursor: "pointer",
            mb: 2,
          }}
          onClick={() => setAuthorImagePickerOpen(true)}
        >
          {authorImage.ImageUrl ? (
            <img
              src={authorImage.ImageUrl}
              alt="Author"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Select Author Image
            </Typography>
          )}
        </Box>
        {error?.[0]?.ImageUrl && (
          <Typography variant="body2" color="error">
            {error[0].ImageUrl}
          </Typography>
        )}

        {/* Section 2: Testimonial Content */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Testimonial Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <GenericInput
              type="text"
              name="Title"
              title="Author Name"
              value={testimonialContent.Title}
              error={error?.[1]?.Title || ""}
              onChange={(e) => handleContentChange("Title", e)}
              onBlur={() => onblur("Title", 1)}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GenericInput
              type="text"
              name="Subtitle"
              title="Author Subtitle"
              value={testimonialContent.Subtitle}
              error={error?.[1]?.Subtitle || ""}
              onChange={(e) => handleContentChange("Subtitle", e)}
              onBlur={() => onblur("Subtitle", 1)}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GenericTextAreaInput
              name="Description"
              title="Testimonial Description"
              value={testimonialContent.Description}
              error={error?.[1]?.Description || ""}
              onChange={(e) => handleContentChange("Description", e)}
              onBlur={() => onblur("Description", 1)}
              disabled={false}
            />
          </Grid>
        </Grid>
      </CardContent>

      {mainImagePickerOpen && (
        <ImagePicker
          open={mainImagePickerOpen}
          multiSelect={false}
          onClose={() => setMainImagePickerOpen(false)}
          onSelect={handleMainImageChange}
        />
      )}
      {authorImagePickerOpen && (
        <ImagePicker
          open={authorImagePickerOpen}
          multiSelect={false}
          onClose={() => setAuthorImagePickerOpen(false)}
          onSelect={handleAuthorImageChange}
        />
      )}
    </Card>
  );
};

export default TestimonialV1Item;
